import {
  Box,
  Flex,
  Text,
  Tooltip,
  useColorModeValue,
  useMediaQuery,
} from '@chakra-ui/react';
import Select, { components } from 'react-select';
import React, { useState, useRef } from 'react';
import EventsRenderStatus from '../client/events/EventsRenderStatus';
import PassesRenderStatus from '../client/passes/PassesRenderStatus';
import ReadersRenderStatus from '../client/readers/ReadersRenderStatus';
import classes from '../../utils/cssHelperClasses.module.css';
import { CustomExclamationMarkInverseIcon } from '../../theme/icons/CustomExclamationMarkInverseIcon';

export const CustomReactSelect = React.forwardRef((props, ref) => {
  const modeInputBg = useColorModeValue(
    '#fff',
    props.liveEventData ? '#2F343D' : '#000000'
  );
  const modePlaceholderColor = useColorModeValue(
    'rgba(59, 72, 81, 0.6)',
    props.liveEventData ? '#ddd' : '#7F8488'
  );
  const modeMenuBg = useColorModeValue('#fff', '#2F343D');
  const modeMenuColor = useColorModeValue('#000000', '#fff');
  const modeMenuBgHover = useColorModeValue(
    'rgba(221, 221, 221, 0.5)',
    '#000000'
  );
  const modeMenuColorDisabled = useColorModeValue('#ddd', '#5A6066');
  const modeBorderHover = useColorModeValue('#2F343D', '#fff');
  const [isSmallerThan320px] = useMediaQuery('(max-width: 320px)');
  const modeInputBgPassStatus = useColorModeValue(
    'rgba(221, 221, 221, 0.5)',
    '#000000'
  );
  const bgTooltip = useColorModeValue('secondaryDark', '#dddddd');
  const textTooltip = useColorModeValue('#ffffff', 'primaryDark');

  // select pass status styles
  const reactSelectPassFilterStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '0px 10px',
      marginTop: '10px',
      backgroundColor:
        props.variant === 'light' ? 'white' : modeInputBgPassStatus,
      display: 'flex',
      borderRadius: '12px',
      border: '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: 'white',
      backgroundColor: props.isDisabled ? '#B8BCBF' : '#FA7D6F',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: 'white',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: 'white',
      ':hover': {
        color: 'white',
      },
    }),
  };

  // select reader styles
  const reactSelectReaderStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '4px 10px',
      backgroundColor: state.isDisabled ? '#E0E0E0' : modeInputBg,
      display: 'flex',
      borderRadius: '12px',
      border: '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      wordBreak: 'break-word',
      ':hover': {
        backgroundColor: !state.data.setupConfirmed ? 'none' : modeMenuBgHover,
        color: !state.data.setupConfirmed ? 'none' : modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
      borderRadius: '10px',
      border: 'none',
      boxShadow: 'none',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
      boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
      borderRadius: '6px',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      backgroundColor: '#DDDD',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      ':hover': {
        color: '#2F343D',
      },
    }),
  };

  // select event and template styles
  const reactSelectPassTemplateStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '4px 16px',
      display: 'flex',
      borderRadius: '6px',
      width: '200px',
      border: '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      backgroundColor: '#DDDD',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      ':hover': {
        color: '#2F343D',
      },
    }),
  };

  // select pass template entity and type styles
  const reactSelectPassTemplateEntityTypeStyles = {
    singleValue: (provided, state) => ({
      ...provided,
      color: !props.liveEventData ? modeMenuColor : modeBorderHover,
      height: props.liveEventData ? '18px' : '',
      fontSize: props.liveEventData && '14px',
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: props.liveEventData ? '36px' : '',
      height: '50px',
      padding: props.liveEventData ? '2px 8px' : '4px 10px',
      backgroundColor: modeInputBg,
      borderRadius: '12px',
      border: '2px solid transparent',
      boxShadow: 'none',
      _placeholder: {
        color: 'rgba(59, 72, 81, 0.6)',
      },
      width:
        props.liveEventData &&
        !props.dashboardPassTemplate &&
        !isSmallerThan320px
          ? '310px'
          : props.dashboardPassTemplate
            ? '200px'
            : '',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? modeMenuColorDisabled : modeMenuColor,
      backgroundColor: modeMenuBg,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.5 : 1,
      ':hover': {
        backgroundColor: state.isDisabled ? modeMenuBg : modeMenuBgHover,
        color: state.isDisabled ? modeMenuColorDisabled : modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      zIndex: '20',
      borderRadius: '10px',
      border: 'none',
      boxShadow: 'none',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
      boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
      borderRadius: '6px',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: !props.passTemplate ? '10px' : '0',
      paddingTop: '0',
      padding: '0',
      paddingBottom: props.liveEventData && '12px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: props.liveEventData ? '13px' : '1em',
        height: props.liveEventData ? '13px' : '1em',
      },
    }),
  };

  const reactSelectBarcodeStyles = {
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? modePlaceholderColor : modeMenuColor,
      height: props.liveEventData ? '18px' : '',
      fontSize: props.liveEventData && '14px',
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: props.liveEventData ? '36px' : '',
      height: '50px',
      padding: props.liveEventData ? '2px 8px' : '4px 10px',
      backgroundColor: state.isDisabled ? modeMenuColorDisabled : modeInputBg,
      borderRadius: '12px',
      border: '2px solid transparent',
      boxShadow: 'none',
      width:
        props.liveEventData &&
        !props.dashboardPassTemplate &&
        !isSmallerThan320px
          ? '310px'
          : props.dashboardPassTemplate
            ? '200px'
            : '',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? modeMenuColorDisabled : modeMenuColor,
      backgroundColor: modeMenuBg,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.5 : 1,
      ':hover': {
        backgroundColor: state.isDisabled ? modeMenuBg : modeMenuBgHover,
        color: state.isDisabled ? modeMenuColorDisabled : modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      zIndex: '20',
      borderRadius: '10px',
      border: 'none',
      boxShadow: 'none',
      maxHeight: '110px',
      overflowY: 'auto',
      pointerEvents: 'auto',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
      boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
      borderRadius: '6px',
      maxHeight: '110px',
      overflowY: 'auto',
      pointerEvents: 'auto',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: !props.passTemplate ? '10px' : '0',
      paddingTop: '0',
      padding: '0',
      paddingBottom: props.liveEventData && '12px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: props.liveEventData ? '13px' : '1em',
        height: props.liveEventData ? '13px' : '1em',
      },
    }),
  };

  const reactSelectWebhookEventsStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '5px 10px',
      height: 'fit-content',
      display: 'flex',
      borderRadius: '12px',
      border: '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: 'white',
      backgroundColor: props.isDisabled ? '#B8BCBF' : '#FA7D6F',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: 'white',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: 'white',
      ':hover': {
        color: 'white',
      },
    }),
  };

  const reactSelectMoreFiltersStyles = {
    singleValue: (provided) => ({
      ...provided,
      color: '#505E69',
      fontSize: '13px',
    }),
    control: (provided, state) => ({
      ...provided,
      marginTop: '10px',
      minHeight: '40px',
      height: '35px',
      padding: '0 12px',
      backgroundColor: state.menuIsOpen ? '#F8F8F8' : '#FFFFFF',
      borderRadius: state.menuIsOpen ? '5px 5px 0 0' : '5px',
      border: 'none',
      borderColor: 'transparent',
      boxShadow: 'none',
      cursor: 'pointer',
      ':hover': {
        border: 'none',
        borderColor: 'transparent',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
      height: '35px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'white' : '#F8F8F8',
      color: state.isSelected ? '#505E69' : '#989898',
      fontSize: '13px',
      height: '35px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: 'white',
        color: '#505E69',
      },
      ':active': {
        backgroundColor: 'white',
      },
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: '0',
      borderRadius: '0 0 5px 5px',
      border: 'none',
      boxShadow: 'none',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
      borderRadius: '0 0 5px 5px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#989898',
      fontSize: '13px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0',
      color: '#989898',
      transition: 'transform 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
      ':hover': {
        color: '#989898',
        cursor: 'pointer',
      },
      svg: {
        width: '14px',
        height: '14px',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      margin: '0',
      padding: '0',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#505E69',
      fontSize: '13px',
      padding: '0',
    }),
    multiValueRemove: () => ({
      display: 'none',
    }),
  };

  const reactSelectPassIdSearchBarStyles = {
    control: (provided, state) => ({
      ...provided,
      marginLeft: '0px',
      padding: '0px 10px',
      backgroundColor: modeInputBgPassStatus,
      width: '150px',
      minWidth: '150px',
      minHeight: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '12px',
      border: '2px solid #E0E0E0',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),

    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
  };

  // custom formatting the multi readers display on create event page
  const MultiValue = (props) => {
    return (
      <components.MultiValue {...props}>
        <div>{props.data.label}</div>
      </components.MultiValue>
    );
  };

  const reactSelectInviteeEntityStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '4px 16px',
      margin: '0 0 0 2rem',
      display: 'flex',
      borderRadius: '12px',
      backgroundColor: '#ebebeb',
      border: props.isInvalid ? '2px solid red' : '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
      width: '50%',
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      backgroundColor: '#DDDD',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      ':hover': {
        color: '#2F343D',
      },
    }),
  };

  const reactSelectChangeRoleStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '4px 16px',
      margin: '16px 0',
      display: 'flex',
      borderRadius: '12px',
      backgroundColor: '#ebebeb',
      border: props.isInvalid ? '2px solid red' : '2px solid transparent',
      boxShadow: 'none',
      ':hover': {
        borderColor: modeBorderHover,
        transition: 'all .3s ease-in-out',
        cursor: 'pointer',
      },
      width: '50%',
    }),
    option: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      backgroundColor: modeMenuBg,
      ':hover': {
        backgroundColor: modeMenuBgHover,
        color: modeMenuColor,
      },
    }),
    menu: (provided, state) => ({
      ...provided,
      // zIndex: '20',
    }),
    menuList: (provided, state) => ({
      ...provided,
      padding: '0px',
      zIndex: '5',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: modePlaceholderColor,
      fontSize: '14px',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    indicatorSeparator: (provided, state) => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      paddingRight: '0px',
      color: modeMenuColor,
      ':hover': {
        color: modeMenuColor,
        cursor: 'pointer',
      },
      svg: {
        width: '1em',
        height: '1em',
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      backgroundColor: '#DDDD',
      borderRadius: '12px',
      padding: '0px 5px',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: '#2F343D',
      ':hover': {
        color: '#2F343D',
      },
    }),
  };

  // Custom Option component with tooltip support
  const CustomOption = (props) => {
    const { data, ...rest } = props;
    const [isHovered, setIsHovered] = useState(false);
    const optionRef = useRef(null);

    if (data.tooltip) {
      return (
        <Box
          display="flex"
          alignItems="center"
          color={modeMenuColor}
          backgroundColor={isHovered ? '#EEEEEE' : modeMenuBg}
          onMouseEnter={() => {
            setIsHovered(true);
            // Find and style the option element directly
            if (optionRef.current) {
              const optionElement =
                optionRef.current.querySelector('[role="option"]');
              if (optionElement) {
                optionElement.style.backgroundColor = '#EEEEEE';
              }
            }
          }}
          onMouseLeave={() => {
            setIsHovered(false);
            // Reset the option element style
            if (optionRef.current) {
              const optionElement =
                optionRef.current.querySelector('[role="option"]');
              if (optionElement) {
                optionElement.style.backgroundColor = modeMenuBg;
              }
            }
          }}
          _hover={{
            backgroundColor: 'EEEEEE',
            color: modeMenuColor,
          }}
          ref={optionRef}
        >
          <components.Option {...rest} data={data} />
          <Tooltip
            label={data.tooltip}
            placement="right"
            hasArrow
            bg={bgTooltip}
            color={textTooltip}
            fontSize="sm"
            px="10px"
            py="5px"
            borderRadius="6px"
          >
            <Box
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="10px"
              borderRadius="4px"
              p="2px"
            >
              <CustomExclamationMarkInverseIcon boxSize="15px" mode={'light'} />
            </Box>
          </Tooltip>
        </Box>
      );
    }

    // For options without tooltip, just render the normal option
    return <components.Option {...rest} data={data} />;
  };

  const MoreFiltersValueContainer = ({ children, ...containerProps }) => {
    const selectedValues = containerProps.getValue();
    // Keep the input element (last child) so blur/outside-click still works
    const input = React.Children.toArray(children).find((child) => {
      if (!React.isValidElement(child)) return false;
      if (child.props?.type === 'input') return true;
      if (child.type === components.Input) return true;
      return false;
    });
    return (
      <components.ValueContainer {...containerProps}>
        {selectedValues.length > 0 ? (
          <>
            <Text
              fontSize="13px"
              color="#505E69"
              fontFamily="Inter, sans-serif"
              isTruncated
            >
              {selectedValues[0].label}
              {selectedValues.length > 1 && `, (+${selectedValues.length - 1})`}
            </Text>
            {React.Children.toArray(children).slice(-1)}
          </>
        ) : (
          children
        )}
      </components.ValueContainer>
    );
  };

  const MoreFiltersOption = (optionProps) => {
    const { isSelected, children, innerRef, innerProps } = optionProps;
    return (
      <Flex
        ref={innerRef}
        {...innerProps}
        align="center"
        gap="8px"
        px="8px"
        h="35px"
        bg={isSelected ? 'white' : '#F8F8F8'}
        color={isSelected ? '#505E69' : '#989898'}
        fontSize="13px"
        cursor="pointer"
        _hover={{ bg: 'white', color: '#505E69' }}
      >
        <Box
          w="12px"
          h="12px"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {isSelected && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M10 3L4.5 8.5L2 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Text fontSize="13px" fontFamily="Inter, sans-serif">
          {children}
        </Text>
      </Flex>
    );
  };

  return (
    <Select
      components={{
        MultiValue,
        ...(props.id === 'idSearchType' && { Option: CustomOption }),
        ...(props.moreFilters && {
          Option: MoreFiltersOption,
          ValueContainer: MoreFiltersValueContainer,
        }),
      }}
      ref={ref}
      {...props}
      styles={
        props.moreFilters
          ? reactSelectMoreFiltersStyles
          : props.id === 'reader'
            ? reactSelectReaderStyles
            : props.id === 'readerType'
              ? reactSelectReaderStyles
              : props.id === 'passTemplate'
                ? reactSelectPassTemplateStyles
                : props.id === 'webhookEvents'
                  ? reactSelectWebhookEventsStyles
                  : (props.id === 'passTemplateFilter') |
                      (props.id === 'groupTagFilter') |
                      (props.id === 'passStatus') |
                      (props.id === 'passTypeFilter') |
                      (props.id === 'readerTypeFilter') |
                      (props.id === 'eventFilter') |
                      (props.id === 'statusFilter') |
                      (props.id === 'deviceTypeFilter')
                    ? reactSelectPassFilterStyles
                    : (props.id === 'templateEntity') |
                        (props.id === 'passType') |
                        (props.id === 'event') |
                        (props.id === 'deviceType') |
                        (props.id === 'readerType')
                      ? reactSelectPassTemplateEntityTypeStyles
                      : props.id === 'entity'
                        ? reactSelectInviteeEntityStyles
                        : (props.id === 'change-entity') |
                            (props.id === 'change-role')
                          ? reactSelectChangeRoleStyles
                          : (props.id === 'barcodeType') |
                              (props.id === 'barcodeSource')
                            ? reactSelectBarcodeStyles
                            : props.id === 'idSearchType'
                              ? reactSelectPassIdSearchBarStyles
                              : ''
      }
      isSearchable={!!props.passTemplate}
      isClearable={!!props.rewardsInfoField || !!props.passTemplate}
      hideSelectedOptions={props.moreFilters ? false : undefined}
      closeMenuOnSelect={props.moreFilters ? false : undefined}
    />
  );
});

// select reader tag label formatting
export const formatOptionLabelSelectReader = ({
  label,
  setupConfirmed,
  enabled,
}) => (
  <Flex justifyContent="space-between">
    <Text wordBreak="break-word" maxW="70%">
      {label}
    </Text>
    <Box>
      <ReadersRenderStatus
        setupConfirmed={setupConfirmed}
        readerStatus={enabled}
      />
    </Box>
  </Flex>
);

// select event tag label formatting
export const formatOptionLabelSelectEvent = ({ name, status }) => {
  if (name.length < 25) {
    return (
      <Flex justifyContent="space-between">
        <Text maxW="60%" className={classes.mobileName}>
          {name}
        </Text>
        <Box>
          <EventsRenderStatus value={status} />
        </Box>
      </Flex>
    );
  } else {
    const trimmedName = name.substring(0, 22);
    return (
      <Flex justifyContent="space-between">
        <Text maxW="60%" className={classes.mobileName}>
          {trimmedName} ...
        </Text>
        <Box>
          <EventsRenderStatus value={status} />
        </Box>
      </Flex>
    );
  }
};

// select event on live event data formatting
export const formatOptionLabelSelectEventOnLiveEventData = ({ name, uuid }) => {
  if (name.length < 35) {
    return <Text value={uuid}>{name}</Text>;
  } else {
    const trimmedName = name.substring(0, 25);
    return <Text value={uuid}>{trimmedName} ...</Text>;
  }
};

// select pass template filter dropdown label formatting for Pass Create page
export const formatOptionLabelSelectPassTemplate = ({ templateName }) => {
  if (templateName?.length < 30) {
    return (
      <Text fontSize="14px" value={templateName}>
        {templateName}
      </Text>
    );
  } else {
    // const trimmedName = name.substring(0, 33);
    // return (
    //   <Text fontSize="14px" value={name}>
    //     {trimmedName} ...
    //   </Text>
    // );
  }
};
// select pass template tag in filter dropdown label formatting
export const formatOptionLabelFilterPassTemplate = ({ label }) => {
  return (
    <Text fontSize="14px" value={label}>
      {label}
    </Text>
  );
};

export const formatOptionLabelFilterEntity = ({ label }) => {
  return (
    <Text fontSize="12px" value={label} isTruncated>
      {label}
    </Text>
  );
};

export const formatOptionLabelSelectRole = ({ value, label }) => {
  return (
    <Text fontSize="12px" value={value} isTruncated>
      {label}
    </Text>
  );
};

export const formatOptionLabelFilterGroupTag = ({ label }) => {
  return (
    <Text fontSize="14px" value={label}>
      {label}
    </Text>
  );
};

// formatting for event filter
export const formatOptionLabelFilterEvent = ({ attributes }) => {
  const { name } = attributes;

  if (name?.length < 30) {
    return (
      <Text fontSize="14px" value={name}>
        {name}
      </Text>
    );
  } else {
    const trimmedName = name.substring(0, 33);
    return (
      <Text fontSize="14px" value={name}>
        {trimmedName} ...
      </Text>
    );
  }
};

// select pass status tag in filter dropdown label formatting
export const formatOptionLabelSelectPassStatus = ({ status }) => (
  <Box>
    <PassesRenderStatus value={status} />
  </Box>
);

// select entity on pass template create page
export const formatOptionLabelSelectEntity = ({ label, value }) => {
  if (label.length < 30) {
    return (
      <Text className={classes.mobileName} value={value}>
        {label}
      </Text>
    );
  } else {
    const trimmedName = label.substring(0, 33);
    return (
      <Text className={classes.mobileName} value={value}>
        {trimmedName} ...
      </Text>
    );
  }
};

// select pass type on pass template create page
export const formatOptionLabelSelectPassType = ({ label, value }) => {
  return (
    <Text className={classes.mobileName} value={value}>
      {label}
    </Text>
  );
};

export const formatOptionLabelSelectDeviceType = ({ label, value }) => {
  return (
    <Text className={classes.mobileName} value={value}>
      {label}
    </Text>
  );
};

export const formatOptionLabelSelectBarcode = ({ label, value }) => {
  return (
    <Text className={classes.mobileName} value={value}>
      {label}
    </Text>
  );
};
